import { createContext, useReducer } from 'react';
import type { ReactElement, ReactNode } from 'react';

import Notifications, { NotificationType } from 'models/Notifications';
import type { INotification } from 'models/Notifications';
import FetchState from 'models/FetchState';

type Props = { children: ReactNode; };
type Dispatcher = (action: Action) => void;

type Action =
  | { type: 'addNotifications'; notifications: Notifications }
  | { type: 'addNotification'; notification: INotification }
  | { type: 'removeNotifications'; notificationType?: NotificationType }
  | { type: 'removeNotification'; id: number }
  | { type: 'reload'; data: INotification[]; }
  | { type: 'setFetchState'; data: FetchState; };

type ContextData = {
  notifications: Notifications;
  fetchState: FetchState;
};

type ContextValue = ContextData & { dispatch: Dispatcher };

const NotificationsContext = createContext<ContextValue | null>(null);

function notificationsReducer(
  value: ContextValue,
  action: Action,
): ContextData {
  const { notifications, fetchState } = value;

  switch (action.type) {
    case 'addNotifications': {
      const { notifications: list } = notifications;
      const { notifications: newList } = action.notifications;

      return {
        fetchState,
        notifications: new Notifications(newList.concat(list)),
      };
    }

    case 'addNotification': {
      const { notifications: list } = notifications;

      return {
        fetchState,
        notifications: new Notifications([action.notification].concat(list)),
      };
    }

    case 'removeNotifications': {
      const { notifications: list } = notifications;

      return {
        fetchState,
        notifications: action.notificationType
          ? new Notifications(list.filter(
            (item) => item.notification_type !== action.notificationType,
          ))
          : new Notifications([]),
      };
    }

    case 'removeNotification': {
      const { notifications: list } = notifications;

      return {
        fetchState,
        notifications: new Notifications(
          list.filter((item) => item.id !== action.id),
        ),
      };
    }

    case 'reload': {
      return {
        fetchState,
        notifications: new Notifications(action.data),
      };
    }

    case 'setFetchState': {
      return { notifications, fetchState: action.data };
    }

    default: {
      return value;
    }
  }
}

function NotificationsProvider({ children }: Props): ReactElement {
  const [value, dispatch] = useReducer(notificationsReducer, {
    fetchState: FetchState.IN_PROGRESS,
    notifications: new Notifications(),
  });

  const { notifications, fetchState } = value;

  return (
    <NotificationsContext.Provider value={{ notifications, dispatch, fetchState }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export type { ContextValue };
export { NotificationsContext, NotificationsProvider };
